import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, clearToken, getToken, BACKEND_URL } from "@/lib/api";

const AuthContext = createContext({});

function extractSessionToken() {
  // Token may arrive via query (?session_token=) — robust across proxies/CDNs —
  // or via a legacy URL hash (#session_token=), kept for backward compatibility.
  try {
    const q = new URLSearchParams(window.location.search);
    const fromQuery = q.get("session_token");
    if (fromQuery) return fromQuery;
  } catch {}
  const m = (window.location.hash || "").match(/session_token=([^&]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signingIn, setSigningIn] = useState(false);

  const bootstrap = useCallback(async () => {
    // Session token may arrive on the URL (query or hash) after the OAuth redirect.
    const fromUrl = extractSessionToken();
    if (fromUrl) {
      setToken(fromUrl);
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("session_token");
        url.hash = "";
        window.history.replaceState(null, "", url.pathname + (url.search || ""));
      } catch {}
    }
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      // Validate the stored token and silently rotate it if it's nearing expiry,
      // so an active user never re-does Google sign-in. Falls straight into the app.
      const { session_token, user: me } = await api.session();
      if (session_token && session_token !== token) setToken(session_token);
      setUser(me);
    } catch {
      clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = useCallback((returnPath = "/") => {
    setSigningIn(true);
    // REMINDER: DO NOT HARDCODE THE URL — derive from current origin.
    const origin = window.location.origin;
    const next = encodeURIComponent(returnPath || "/");
    window.location.href = `${BACKEND_URL}/api/auth/google/login?return=${encodeURIComponent(origin)}&next=${next}`;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } catch {}
    clearToken();
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      setUser(await api.me());
    } catch {}
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, signingIn, login, signOut, setUser, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
